/* ByteDepot — Gallery UX enhancements
   Adds scroll-wheel navigation and mouse drag/swipe to the product media gallery.
   Loads after media-gallery.js (defer) so this.elements is already populated.

   Design notes:
   - getSlides() queries only within gallery.elements.viewer (not the thumbnail
     strip) so the mediaIds passed to setActiveMedia() always exist in the viewer.
   - Document-level pointermove/pointerup listeners are used instead of
     setPointerCapture so that child elements (e.g. product-modal zoom opener)
     still receive their click events normally. */
(function () {
  'use strict';

  function enhanceGallery(gallery) {
    var viewer = gallery.elements && gallery.elements.viewer;
    if (!viewer) return;

    /* Only the top-level li slide wrappers, which hold the full prefixed mediaId
       that setActiveMedia() expects (e.g. "template--...main-XXXX").
       Inner media elements also carry data-media-id but with only the short numeric
       ID — passing those to setActiveMedia() causes a null-reference crash. */
    function getSlides() {
      return Array.from(viewer.querySelectorAll('li[data-media-id]'));
    }

    function getCurrentIndex() {
      return getSlides().findIndex(function (el) {
        return el.classList.contains('is-active');
      });
    }

    function navigateTo(delta) {
      var slides = getSlides();
      var next = getCurrentIndex() + delta;
      if (next < 0 || next >= slides.length) return;
      gallery.setActiveMedia(slides[next].dataset.mediaId, false);
    }

    /* ── Scroll wheel: next/prev image ──────────────────────────── */
    var wheelLock = false;
    viewer.addEventListener(
      'wheel',
      function (e) {
        e.preventDefault();
        if (wheelLock) return;
        wheelLock = true;
        setTimeout(function () {
          wheelLock = false;
        }, 350);
        navigateTo(e.deltaY > 0 ? 1 : -1);
      },
      { passive: false },
    );

    /* ── Pointer drag / touch swipe ─────────────────────────────── */
    /* Uses document-level listeners — NOT setPointerCapture — so that
       child click events (zoom modal opener) still fire normally. */
    var originX = null;
    var isDragging = false;
    var wasDrag = false; /* blocks the click that fires right after a drag */

    viewer.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      originX = e.clientX;
      isDragging = false;
      wasDrag = false;
      viewer.style.cursor = 'grabbing';

      function onMove(evt) {
        if (originX !== null && Math.abs(evt.clientX - originX) > 8) {
          isDragging = true;
        }
      }

      function cleanup() {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onCancel);
        viewer.style.cursor = 'grab';
      }

      function onUp(evt) {
        var dx = evt.clientX - (originX !== null ? originX : evt.clientX);
        originX = null;
        cleanup();
        if (!isDragging || Math.abs(dx) < 40) {
          isDragging = false;
          return;
        }
        wasDrag = true;
        isDragging = false;
        navigateTo(dx < 0 ? 1 : -1);
      }

      function onCancel() {
        cleanup();
        originX = null;
        isDragging = false;
      }

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onCancel);
    });

    /* Block the click that fires immediately after a completed drag gesture.
       Capture phase so it runs before Dawn's modal-opener listener. */
    viewer.addEventListener(
      'click',
      function (e) {
        if (wasDrag) {
          e.preventDefault();
          e.stopImmediatePropagation();
          wasDrag = false;
        }
      },
      true,
    );

    viewer.style.cursor = 'grab';
  }

  function initAll() {
    document.querySelectorAll('media-gallery').forEach(enhanceGallery);
  }

  if (customElements.get('media-gallery')) {
    /* Already defined — run after a tick so constructor has populated elements */
    setTimeout(initAll, 0);
  } else {
    customElements.whenDefined('media-gallery').then(function () {
      setTimeout(initAll, 0);
    });
  }
})();
